import { Loader2, Terminal } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '~/api/trpc';
import {
    AppointmentHistoryTable,
    HighlightedOnlySwitch,
    isHighlightedEntry,
    countCalledOff,
    countLateCancellations,
    countLateMoves,
    countMovedByKalia,
    HistoryStatCard,
    LATE_THRESHOLD_HOURS,
} from '~/components/appointments/appointmentHistory';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';

type AppointmentHistoryModalProps = {
    appointmentId: string;
    open: boolean;
    onClose: () => void;
};

export function AppointmentHistoryModal({ appointmentId, open, onClose }: AppointmentHistoryModalProps) {
    const historyQuery = trpc.appointments.getAppointmentModificationHistory.useQuery(
        { appointmentId },
        { enabled: open }
    );

    const [highlightedOnly, setHighlightedOnly] = useState(false);

    const entries = historyQuery.data ?? [];
    const visibleEntries = highlightedOnly ? entries.filter(isHighlightedEntry) : entries;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Verlauf</DialogTitle>
                    <DialogDescription>Verschiebungen und Statusänderungen dieses Termins</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                    <HistoryStatCard
                        label="Durch Kalia verschoben oder abgesagt"
                        count={countCalledOff(entries) + countMovedByKalia(entries)}
                        tone="yellow"
                    />
                    <HistoryStatCard
                        label={`Kurzfristig verschoben oder abgesagt durch Kunde (< ${LATE_THRESHOLD_HOURS}h)`}
                        count={countLateMoves(entries) + countLateCancellations(entries)}
                        tone="red"
                    />
                </div>

                <HighlightedOnlySwitch checked={highlightedOnly} onCheckedChange={setHighlightedOnly} />

                {historyQuery.isError && (
                    <Alert variant="destructive">
                        <Terminal className="h-4 w-4" />
                        <AlertTitle>Fehler beim Laden des Verlaufs</AlertTitle>
                        <AlertDescription>{historyQuery.error.message}</AlertDescription>
                    </Alert>
                )}

                {historyQuery.isLoading ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : visibleEntries.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">Keine Änderungen vorhanden</p>
                ) : (
                    <AppointmentHistoryTable entries={visibleEntries} />
                )}
            </DialogContent>
        </Dialog>
    );
}
